import useSWR from 'swr';

import { fetchEndpointData } from '@/utils/fetch.client';

/**
 * The project's dev server, as the sandbox's browser sidecar reports it.
 *
 * Every call here resumes the sandbox (the routes go through `ensure_session_sandbox_live`), so
 * callers must gate on the session actually running — see `DevServerStrip`. That is the same
 * bargain the ports poll already makes, but it is a bargain, not a freebie.
 */
export interface DevServerStatus {
  /**
   * `starting` is a real, durable state, not a client-side guess: the sidecar spawns the process,
   * records it, and keeps watching after the HTTP call that asked for it has returned. That is
   * what stops a cold compile from having to fit inside one request.
   */
  state: 'ready' | 'stopped' | 'starting' | 'failed';
  port: number | null;
  url: string | null;
  command?: string;
  /** Why it failed, with a log tail. Only on `failed`. */
  error?: string;
  /** Where the launch config came from — `external` means someone else started it. */
  resolvedFrom?: 'config' | 'argument' | 'detected' | 'external';
  adopted?: 'ours' | 'external';
  note?: string;
}

export function devServerKey(base: string): string {
  return `${base}/dev-server`;
}

/**
 * Poll only while `enabled`.
 *
 * `refreshInterval` matches the ports poll the Preview tab already runs, so mounting the strip
 * adds a second stream of the same cadence rather than a new class of traffic — and stops
 * entirely when the sandbox is not running.
 */
export function useDevServerStatus(base: string, enabled: boolean) {
  const { data, error, isLoading, mutate } = useSWR<DevServerStatus>(
    enabled ? devServerKey(base) : null,
    (key: string) => fetchEndpointData<DevServerStatus>(key),
    { refreshInterval: 5000, revalidateOnFocus: false },
  );
  return { status: data ?? null, error, isLoading, mutate };
}

/** The dev server's recent output. Only fetched while the disclosure is open. */
export function useDevServerLogs(base: string, enabled: boolean, tail = 200) {
  const { data, isLoading } = useSWR<DevServerStatus & { logs: string }>(
    enabled ? `${base}/dev-server/logs?tail=${tail}` : null,
    (key: string) => fetchEndpointData<DevServerStatus & { logs: string }>(key),
    { refreshInterval: 5000, revalidateOnFocus: false },
  );
  return { logs: data?.logs ?? '', error: data?.error, isLoading };
}

export async function startDevServer(base: string): Promise<DevServerStatus> {
  return fetchEndpointData<DevServerStatus>(`${base}/dev-server/start`, { method: 'POST', body: {} });
}

export async function stopDevServer(base: string): Promise<DevServerStatus> {
  return fetchEndpointData<DevServerStatus>(`${base}/dev-server/stop`, { method: 'POST', body: {} });
}

export async function restartDevServer(base: string): Promise<DevServerStatus> {
  return fetchEndpointData<DevServerStatus>(`${base}/dev-server/restart`, { method: 'POST', body: {} });
}
